import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, Download, GitMerge, History, Home, Layers, RefreshCw, Search, Users, X } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, downloadBlob, fmtDateTime } from "../lib/api";
import { PaginationBar } from "./PaginationBar";
import { StaffLabel } from "./StaffAvatar";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const LEVEL_STYLE = {
  zones: "border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]",
  wards: "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
  mohallas: "border-amber-300 bg-amber-50 text-amber-900",
};

/** Small coloured tag for the merge level (Zone / Ward / Mohalla). */
function LevelBadge({ level, label, testId }) {
  return <Badge variant="outline" className={`text-[10px] font-medium ${LEVEL_STYLE[level] || ""}`} data-testid={testId}>{label || level}</Badge>;
}

/** "Removed → Kept" with the parent in brackets when it differs or when the merge crossed parents. */
function MergePair({ item, compact = false }) {
  const s = item.source || {}, t = item.target || {};
  const showParents = !!(s.parent_name || t.parent_name);
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${compact ? "text-xs" : "text-sm"}`}>
      <span className="line-through decoration-muted-foreground/60 text-muted-foreground" title="Removed (duplicate)">{s.name || "—"}</span>
      {showParents && s.parent_name && <span className="text-[11px] text-muted-foreground">({s.parent_name})</span>}
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="font-medium" title="Kept">{t.name || "—"}</span>
      {showParents && t.parent_name && <span className="text-[11px] text-muted-foreground">({t.parent_name})</span>}
      {item.cross_parent && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-900" title="Source and target sat under different parents">cross-parent</Badge>}
    </span>
  );
}

/**
 * Phase 164 — Master Data › Merge History: every live zone / ward / mohalla merge (newest first) with who ran it,
 * what was merged into what, how many properties moved and which child merges cascaded. Filters by type / text /
 * date, paged, Excel export. Legacy merges that only left an audit row are imported by the backend on first load.
 */
export function MergeHistoryTab({ code }) {
  const [level, setLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  // debounce the free-text search
  useEffect(() => { const t = setTimeout(() => { setNeedle(search.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [search]);

  const params = useMemo(() => ({
    level: level === "all" ? undefined : level, search: needle || undefined,
    date_from: dateFrom || undefined, date_to: dateTo || undefined, page, limit,
  }), [level, needle, dateFrom, dateTo, page, limit]);

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`/t/${code}/masters/merge-history`, { params })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [code, params]);
  useEffect(() => { load(); }, [load]);

  const filtersActive = level !== "all" || !!needle || !!dateFrom || !!dateTo;
  const resetFilters = () => { setLevel("all"); setSearch(""); setNeedle(""); setDateFrom(""); setDateTo(""); setPage(1); };
  const toggle = (id) => setExpanded((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (params.level) qs.set("level", params.level);
      if (params.search) qs.set("search", params.search);
      if (params.date_from) qs.set("date_from", params.date_from);
      if (params.date_to) qs.set("date_to", params.date_to);
      await downloadBlob(`/t/${code}/masters/merge-history/export?${qs.toString()}`, `merge-history_${code}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Merge history downloaded", { description: `${n(data?.total)} merge${data?.total === 1 ? "" : "s"} · Excel` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const t = data?.totals || {};
  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="merge-history-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold font-heading flex items-center gap-2"><History className="h-4 w-4 text-primary" aria-hidden="true" /> Merge History</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every duplicate zone / ward / mohalla that was merged — who ran it, what was kept and how many properties moved. The removed name is struck through; the kept one is bold.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-8" onClick={load} disabled={loading} title="Refresh" data-testid="merge-history-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={exportXlsx} disabled={exporting || !data || data.total === 0} data-testid="merge-history-export">
            <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Preparing…" : "Download Excel"}
          </Button>
        </div>
      </div>

      {/* summary strip for the current filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="merge-history-totals">
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><GitMerge className="h-3.5 w-3.5" aria-hidden="true" /> Merges</p>
          {data ? <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="merge-history-total-merges">{n(t.merges)}</p> : <Skeleton className="h-6 w-14 mt-1" />}
          {data && <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="merge-history-by-level">
            {n(t.by_level?.zones)} zone{t.by_level?.zones === 1 ? "" : "s"} · {n(t.by_level?.wards)} ward{t.by_level?.wards === 1 ? "" : "s"} · {n(t.by_level?.mohallas)} mohalla{t.by_level?.mohallas === 1 ? "" : "s"}
          </p>}
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Home className="h-3.5 w-3.5" aria-hidden="true" /> Properties moved</p>
          {data ? <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="merge-history-total-properties">{n(t.properties)}</p> : <Skeleton className="h-6 w-14 mt-1" />}
          {data && t.properties_trash > 0 && <p className="text-[11px] text-muted-foreground tabular-nums">+ {n(t.properties_trash)} in trash</p>}
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" aria-hidden="true" /> Staff mappings</p>
          {data ? <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="merge-history-total-staff">{n(t.staff)}</p> : <Skeleton className="h-6 w-14 mt-1" />}
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Layers className="h-3.5 w-3.5" aria-hidden="true" /> Child merges</p>
          {data ? <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="merge-history-total-cascades">{n(t.cascades)}</p> : <Skeleton className="h-6 w-14 mt-1" />}
          {data && <p className="text-[11px] text-muted-foreground tabular-nums">{n(t.total_changes)} records updated in all</p>}
        </CardContent></Card>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-end gap-3" data-testid="merge-history-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 h-9" placeholder="Search zone / ward / mohalla or who ran it…" value={search}
            onChange={(e) => setSearch(e.target.value)} aria-label="Search merge history" data-testid="merge-history-search" />
        </div>
        <Select value={level} onValueChange={(v) => { setLevel(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-40" data-testid="merge-history-level-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="zones">Zones</SelectItem>
            <SelectItem value="wards">Wards</SelectItem>
            <SelectItem value="mohallas">Mohallas</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="merge-history-from" className="text-xs text-muted-foreground">From</Label>
          <Input id="merge-history-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="h-9 w-40 cursor-pointer" data-testid="merge-history-date-from" />
        </div>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="merge-history-to" className="text-xs text-muted-foreground">To</Label>
          <Input id="merge-history-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="h-9 w-40 cursor-pointer" data-testid="merge-history-date-to" />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" className="h-9 px-2 text-xs" onClick={resetFilters} data-testid="merge-history-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {error ? (
        <Card className="shadow-sm border-destructive/40" data-testid="merge-history-error">
          <CardContent className="p-4 text-sm text-destructive flex items-center justify-between gap-3">
            <span>Could not load the merge history: {error}</span>
            <Button variant="outline" size="sm" onClick={load} data-testid="merge-history-retry">Retry</Button>
          </CardContent>
        </Card>
      ) : !data && loading ? (
        <Card className="shadow-sm" data-testid="merge-history-loading"><CardContent className="p-4 space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : items.length === 0 ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title={filtersActive ? "No merges match these filters" : "No merges yet"}
            subtitle={filtersActive
              ? "Try a different type, text or date range."
              : "When you merge duplicate zones, wards or mohallas from their Master Data tables, every merge is recorded here with who ran it and how many properties moved."}
            action={filtersActive ? <Button variant="outline" size="sm" onClick={resetFilters} data-testid="merge-history-empty-clear">Clear filters</Button> : null} />
        </CardContent></Card>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="merge-history-table" data-loading={loading ? "true" : "false"}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="whitespace-nowrap">When (IST)</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="min-w-[260px]">Merged → kept</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Properties moved</TableHead>
                  <TableHead className="text-right whitespace-nowrap hidden lg:table-cell">Other records</TableHead>
                  <TableHead className="text-right whitespace-nowrap hidden md:table-cell">Child merges</TableHead>
                  <TableHead>Run by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => {
                  const im = it.impact || {};
                  const casc = it.cascades || [];
                  const cascN = it.cascade_count ?? casc.length;
                  const open = expanded.has(it.id);
                  const other = (im.staff || 0) + (im.wards || 0) + (im.mohallas || 0) + (im.other || 0);
                  const otherBits = [im.staff ? `${n(im.staff)} staff` : null, im.wards ? `${n(im.wards)} ward${im.wards === 1 ? "" : "s"}` : null,
                    im.mohallas ? `${n(im.mohallas)} mohalla${im.mohallas === 1 ? "" : "s"}` : null, im.other ? `${n(im.other)} other` : null].filter(Boolean);
                  return (
                    <React.Fragment key={it.id}>
                      <TableRow className={`hover:bg-muted/60 ${open ? "bg-muted/30" : ""}`} data-testid={`merge-history-row-${it.id}`} data-level={it.level}>
                        <TableCell className="w-8 pr-0">
                          {casc.length > 0 ? (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggle(it.id)} aria-expanded={open}
                              aria-label={open ? "Hide child merges" : "Show child merges"} data-testid={`merge-history-expand-${it.id}`}>
                              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          ) : <span className="inline-block w-7" />}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap tabular-nums" data-testid={`merge-history-when-${it.id}`}>
                          {fmtDateTime(it.created_at)}
                          {it.source_kind === "audit" && (
                            <span className="block text-[10px] text-muted-foreground" title="Recorded before Merge History existed — details recovered from the audit trail" data-testid={`merge-history-legacy-${it.id}`}>from audit log</span>
                          )}
                        </TableCell>
                        <TableCell><LevelBadge level={it.level} label={it.label} testId={`merge-history-level-${it.id}`} /></TableCell>
                        <TableCell data-testid={`merge-history-pair-${it.id}`}><MergePair item={it} /></TableCell>
                        <TableCell className="text-right tabular-nums" data-testid={`merge-history-properties-${it.id}`}>
                          <span className={`font-medium ${im.properties ? "" : "text-muted-foreground"}`}>{n(im.properties)}</span>
                          {im.properties_trash > 0 && <span className="block text-[10px] text-muted-foreground">+ {n(im.properties_trash)} in trash</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs hidden lg:table-cell" data-testid={`merge-history-other-${it.id}`}>
                          {other ? <span title={otherBits.join(" · ")}>{otherBits.join(" · ")}</span> : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs hidden md:table-cell" data-testid={`merge-history-cascades-${it.id}`}>
                          {cascN ? (casc.length ? <button type="button" className="underline-offset-2 hover:underline text-primary" onClick={() => toggle(it.id)}>{n(cascN)}</button> : n(cascN)) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm min-w-[160px]" data-testid={`merge-history-by-${it.id}`}>
                          {it.by?.id ? <StaffLabel code={code} userId={it.by.id} name={it.by.name} empCode={it.by.employee_id} /> : (it.by_name || "System")}
                        </TableCell>
                      </TableRow>
                      {open && casc.length > 0 && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20" data-testid={`merge-history-cascade-rows-${it.id}`}>
                          <TableCell />
                          <TableCell colSpan={7} className="py-2">
                            <p className="text-[11px] text-muted-foreground mb-1.5">Child records merged as part of this {it.label?.toLowerCase()} merge (same-named children of the removed {it.label?.toLowerCase()} folded into the kept one):</p>
                            <ul className="space-y-1">
                              {casc.map((c, i) => {
                                const cim = c.impact || {};
                                return (
                                  <li key={`${it.id}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs" style={{ paddingLeft: `${Math.max(0, (c.depth || 1) - 1) * 16}px` }}
                                    data-testid={`merge-history-cascade-${it.id}-${i}`}>
                                    <LevelBadge level={c.level} label={c.label} />
                                    <MergePair item={c} compact />
                                    <span className="tabular-nums text-muted-foreground">{n(cim.properties)} propert{cim.properties === 1 ? "y" : "ies"}{cim.properties_trash ? ` (+${n(cim.properties_trash)} in trash)` : ""} · {n(c.total_changes)} record{c.total_changes === 1 ? "" : "s"}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {data && data.total > 0 && (
        <PaginationBar page={page} limit={limit} total={data.total} loading={loading}
          onPageChange={setPage} onLimitChange={(v) => { setLimit(v); setPage(1); }} testId="merge-history-pagination" />
      )}
      <p className="text-xs text-muted-foreground">
        Properties moved counts each property once even when its zone and ward both changed in the same merge. Merges run before this log existed are
        recovered from the audit trail and marked <span className="italic">from audit log</span>; their child merges appear as separate rows.
      </p>
    </div>
  );
}
